import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { api, trackEvent } from "../lib/api";
import { toast } from "sonner";

const Input = ({ label, ...props }) => (
  <div>
    <label className="overline text-[10px] block mb-1">{label}</label>
    <input
      {...props}
      className="w-full bg-transparent border-b border-black focus:border-[#FF1493] outline-none py-1.5 font-sans-display text-sm"
    />
  </div>
);

const computePrice = (pricing, size, numScents) => {
  if (!pricing) return 0;
  const sz = pricing.sizes.find((s) => s.label === size) || pricing.sizes[0];
  const base = sz?.price || 0;
  const included = sz?.included_scents ?? pricing.included_scents ?? 1;
  const extras = Math.max(0, numScents - included);
  return Math.round((base + extras * (pricing.extra_scent_price || 0)) * 100) / 100;
};

const EMPTY_FORM = {
  bottle_size: "20ml",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  shipping_street: "",
  shipping_city: "",
  shipping_state: "",
  shipping_zip: "",
  shipping_country: "USA",
  shipping_method: "free",
  promo_code: "",
  notes: "",
};

// Static fallback so the running total is never $0.00 while /shipping-methods
// is still fetching (server is the source of truth once loaded).
const SHIPPING_FALLBACK = { free: 0, standard: 5, expedited: 20 };

const OrderDialog = ({
  open,
  onClose,
  blendName,
  scentIds,
  initialSize,
  pricing: pricingProp,
  onPlaced,
}) => {
  const [pricing, setPricing] = useState(pricingProp || null);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    bottle_size: initialSize || "20ml",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && !pricing) {
      api.get("/pricing").then((r) => setPricing(r.data)).catch((e) => console.error("OrderDialog: /pricing fetch failed", e));
    }
    if (open && shippingMethods.length === 0) {
      api.get("/shipping-methods").then((r) => setShippingMethods(r.data)).catch((e) => console.error("OrderDialog: /shipping-methods fetch failed", e));
    }
  }, [open, pricing, shippingMethods.length]);

  useEffect(() => {
    if (initialSize) {
      setForm((f) => ({ ...f, bottle_size: initialSize }));
    }
  }, [initialSize, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const shippingCost = useMemo(() => {
    const m = shippingMethods.find((x) => x.id === form.shipping_method);
    if (m) return Number(m.price);
    return SHIPPING_FALLBACK[form.shipping_method] ?? 0;
  }, [shippingMethods, form.shipping_method]);

  const blendPrice = useMemo(
    () => computePrice(pricing, form.bottle_size, scentIds.length),
    [pricing, form.bottle_size, scentIds.length]
  );

  const discount = useMemo(() => {
    const code = (form.promo_code || "").trim().toUpperCase();
    if (code === "BRIOLOGY15") {
      return Math.round(blendPrice * 15) / 100;
    }
    return 0;
  }, [form.promo_code, blendPrice]);

  const promoValid =
    (form.promo_code || "").trim().toUpperCase() === "BRIOLOGY15";

  const total = useMemo(
    () => Math.max(0, Math.round((blendPrice + shippingCost - discount) * 100) / 100),
    [blendPrice, shippingCost, discount]
  );

  const submit = async () => {
    if (!blendName?.trim()) {
      toast.error("Name your blend on the bottle first");
      return;
    }
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (
      !form.shipping_street.trim() ||
      !form.shipping_city.trim() ||
      !form.shipping_zip.trim()
    ) {
      toast.error("Full shipping address is required");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await api.post("/orders", {
        blend_name: blendName.trim(),
        scent_ids: scentIds,
        origin_url: window.location.origin,
        ...form,
      });
      trackEvent("checkout_started", { session_id: data.session_id });
      onPlaced && onPlaced({ session_id: data.session_id });
      // Persist the blend name so the success handler can display it after redirect
      try {
        sessionStorage.setItem(
          "briology_pending_checkout",
          JSON.stringify({
            session_id: data.session_id,
            blend_name: blendName.trim(),
            total,
          })
        );
      } catch (e) {
        // sessionStorage may be unavailable in some browsers — non-fatal
        console.error("OrderDialog: sessionStorage write failed", e);
      }
      // Redirect to Stripe hosted checkout
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const stepTitles = ["Size & contact", "Shipping & checkout"];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="order-dialog"
        className="bg-white border border-black rounded-none max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="overline text-[10px] text-[#FF1493]">
            Custom order · step {step} of 2
          </div>
          <DialogTitle className="font-serif-display text-3xl font-black">
            {stepTitles[step - 1]}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Place a custom blend order — enter your details and pay securely
            with Stripe.
          </DialogDescription>
        </DialogHeader>

        {/* Live price summary — visible on every step */}
        <div
          data-testid="price-summary"
          className="bg-[#FFF1F5] border border-black p-3 mt-2"
        >
          <div className="flex items-baseline justify-between">
            <div>
              <div className="overline text-[10px] text-black/70">
                Running total
              </div>
              <div className="font-serif-display text-3xl font-black text-[#FF1493]">
                ${total.toFixed(2)}
              </div>
            </div>
            <div className="text-[11px] text-right text-black/70 leading-tight">
              <div>
                {form.bottle_size} · {scentIds.length} scent
                {scentIds.length === 1 ? "" : "s"}
              </div>
              <div className="italic">
                Blend ${blendPrice.toFixed(2)} + Ship $
                {shippingCost.toFixed(2)}
                {discount > 0 && (
                  <span className="block text-[#FF1493] not-italic font-semibold">
                    − ${discount.toFixed(2)} · Briology15
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {step === 1 && (
          <div className="grid gap-4 mt-4">
            <div>
              <label className="overline text-[10px] block mb-2">
                Bottle size
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(pricing?.sizes || []).map((s) => (
                  <button
                    key={s.label}
                    data-testid={`size-${s.label}`}
                    type="button"
                    onClick={() => set("bottle_size", s.label)}
                    className={[
                      "border border-black py-3 px-4 text-left transition-colors",
                      form.bottle_size === s.label
                        ? "bg-[#FF1493] text-white border-[#FF1493]"
                        : "bg-white hover:bg-black hover:text-white",
                    ].join(" ")}
                  >
                    <div className="font-serif-display text-xl font-black leading-none">
                      {s.label}
                    </div>
                    <div className="overline text-[10px] mt-1">
                      ${s.price}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <Input
              data-testid="order-customer-name"
              label="Full name"
              value={form.customer_name}
              onChange={(e) => set("customer_name", e.target.value)}
              required
            />
            <Input
              data-testid="order-customer-email"
              label="Email"
              type="email"
              value={form.customer_email}
              onChange={(e) => set("customer_email", e.target.value)}
              required
            />
            <Input
              data-testid="order-customer-phone"
              label="Phone (optional)"
              value={form.customer_phone}
              onChange={(e) => set("customer_phone", e.target.value)}
            />
          </div>
        )}

        {step === 2 && (
          <div className="grid gap-4 mt-4">
            <Input
              data-testid="order-street"
              label="Street address"
              value={form.shipping_street}
              onChange={(e) => set("shipping_street", e.target.value)}
              required
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                data-testid="order-city"
                label="City"
                value={form.shipping_city}
                onChange={(e) => set("shipping_city", e.target.value)}
                required
              />
              <Input
                data-testid="order-state"
                label="State"
                value={form.shipping_state}
                onChange={(e) => set("shipping_state", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                data-testid="order-zip"
                label="ZIP"
                value={form.shipping_zip}
                onChange={(e) => set("shipping_zip", e.target.value)}
                required
              />
              <Input
                data-testid="order-country"
                label="Country"
                value={form.shipping_country}
                onChange={(e) => set("shipping_country", e.target.value)}
              />
            </div>
            <div>
              <label className="overline text-[10px] block mb-2">
                Shipping method
              </label>
              <div className="grid gap-2" data-testid="shipping-methods">
                {shippingMethods.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`shipping-${m.id}`}
                    onClick={() => set("shipping_method", m.id)}
                    className={[
                      "flex items-center justify-between border border-black py-2.5 px-3 text-left transition-colors",
                      form.shipping_method === m.id
                        ? "bg-[#FF1493] text-white border-[#FF1493]"
                        : "bg-white hover:bg-black hover:text-white",
                    ].join(" ")}
                  >
                    <div>
                      <div className="font-serif-display text-base font-black leading-tight">
                        {m.label}
                      </div>
                      <div className="overline text-[9.5px] opacity-80">
                        {m.description}
                      </div>
                    </div>
                    <div className="font-serif-display text-lg font-black">
                      ${Number(m.price).toFixed(2)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label
                htmlFor="promo-code"
                className="overline text-[10px] block mb-2"
              >
                Promo code
              </label>
              <div
                data-testid="promo-code-row"
                className="flex items-center gap-2"
              >
                <input
                  id="promo-code"
                  data-testid="promo-code-input"
                  value={form.promo_code}
                  onChange={(e) => set("promo_code", e.target.value)}
                  placeholder="e.g. Briology15"
                  className="flex-1 border border-black px-3 py-2.5 text-sm outline-none focus:bg-[#FFF1F5]"
                />
                {promoValid && (
                  <span
                    data-testid="promo-code-applied"
                    className="overline text-[10px] tracking-[0.22em] text-white bg-[#FF1493] px-3 py-1"
                  >
                    − ${discount.toFixed(2)}
                  </span>
                )}
              </div>
              {form.promo_code && !promoValid && (
                <div className="text-[11px] text-black/50 italic mt-1">
                  Invalid code
                </div>
              )}
            </div>
            <div>
              <label className="overline text-[10px] block mb-1">
                Anything else?
              </label>
              <textarea
                data-testid="order-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                className="w-full border border-black/30 bg-transparent p-2 text-sm outline-none focus:border-[#FF1493]"
              />
            </div>
            <p className="text-[11px] text-black/60 italic">
              You&apos;ll be redirected to a secure Stripe checkout to complete
              payment of ${total.toFixed(2)}.
            </p>
          </div>
        )}

        <div className="flex justify-between items-center pt-6 border-t border-black/15 mt-4">
          <button
            onClick={step === 1 ? onClose : () => setStep(step - 1)}
            className="overline text-[10px] underline-offset-4 hover:underline"
          >
            {step === 1 ? "Cancel" : "← Back"}
          </button>
          {step < 2 ? (
            <button
              data-testid="order-next-btn"
              onClick={() => setStep(step + 1)}
              className="bg-black text-white overline text-[11px] px-5 py-2 hover:bg-[#FF1493]"
            >
              Next →
            </button>
          ) : (
            <button
              data-testid="order-submit-btn"
              disabled={submitting}
              onClick={submit}
              className="bg-[#FF1493] text-white overline text-[11px] px-5 py-2 border border-black hover:bg-black disabled:opacity-50"
            >
              {submitting ? "Redirecting…" : `Pay $${total.toFixed(2)} with Stripe →`}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDialog;
